import mapConnection from '../utils/connectionMapper'
import moment from 'moment'

const requiredForFrenchMissingDocuments = [
  'firstName',
  'flightNumber',
  'flightLocalDepartureDate',
  'flightAirportOriginName',
  'flightAirportDestinationName'
]

const requiredForOtherMissingDocuments = [
  ...requiredForFrenchMissingDocuments,
  'reason'
]

const requiredForLOA = [
  'lastName',
  'firstName',
  'airlineCode',
  'flightNumber',
  'flightAirportOriginName',
  'flightAirportOriginIata',
  'flightAirportDestinationName',
  'flightAirportDestinationIata',
  'flightLocalDepartureDate',
  'standardCompensationAmount',
  'organizationName',
  'LOALinks',
  'affiliate',
  'reason'
]

const checkForMissing = (customer, template) => {
  let missing = []
  if (template === 'sendLOA') {
    missing = requiredForLOA.filter(r => !customer[r])
  }
  if (template === 'missingDocuments') {
    if (customer.language === 'French')
      missing = requiredForFrenchMissingDocuments.filter(r => !customer[r])
    else
      missing = requiredForOtherMissingDocuments.filter(r => !customer[r])
  }
  return missing
}

const emailTemplate = (claim, template) => {
  if(claim) {
    const customer = {
      id: claim.id,
      firstName: claim.passengerTicket.passengerFirstName,
      lastName: claim.passengerTicket.passengerLastName,
      language: claim.passengerTicket.passengerLanguage,
      organizationName: claim.passengerTicket.organizationName,

      airlineCode: claim.flight.airlineCode,
      flightNumber: claim.flight.number,
      flightAirportOriginName: claim.flight.airportOrigin.name,
      flightAirportOriginIata: claim.flight.airportOrigin.iata,
      flightAirportDestinationName: claim.flight.airportDestination.name,
      flightAirportDestinationIata: claim.flight.airportDestination.iata,
      flightLocalDepartureDate: moment(claim.flight.localDepartureDate).format('DD/MM/YYYY'),

      affiliate: claim.affiliate,
      reason: claim.reason,
      standardCompensationAmount: claim.maximumCompensationAmount,
      LOALinks: mapConnection(claim.claimsPassengerTicketConnection).filter(cpt => cpt.loa).map(cpt =>
        cpt.passengerTicket.passengerFirstName + ' '
        + cpt.passengerTicket.passengerLastName + ' '
        + cpt.loa.myclaimUrl
      ).join('\n')
    }

    const warnings = checkForMissing(customer, template);

    if(template === 'sendLOA') {
      switch(customer.language) {
        case 'French':
          return { email: sendLOAFrench(customer), warnings: warnings }
        case 'English':
          return { email: sendLOAEnglish(customer), warnings: warnings }
        case 'Spanish':
          return { email: sendLOASpanish(customer), warnings: warnings }
        case 'German':
          return { email: sendLOAGerman(customer), warnings: warnings }
        case 'Italian':
          return { email: sendLOAItalian(customer), warnings: warnings }
        default:
          return { email: sendLOAEnglish(customer), warnings: warnings }
      }
    }
    if(template === 'missingDocuments') {
      switch(customer.language) {
        case 'French':
          return { email: missingDocumentsFrench(customer), warnings: warnings }
        case 'English':
          return { email: missingDocumentsEnglish(customer), warnings: warnings }
        case 'Spanish':
          return { email: missingDocumentsSpanish(customer), warnings: warnings }
        case 'German':
          return { email: missingDocumentsGerman(customer), warnings: warnings }
        default:
          return { email: missingDocumentsEnglish(customer), warnings: warnings }
      }
    } else {
      return null;
    }
  }
}

const missingDocumentsFrench = (customer) => {
  return (
    `À l'attention de : ${customer.firstName} ${customer.lastName}
Bonjour ${customer.firstName},
Vous avez récemment formulé une demande d'indemnisation sur notre site concernant la perturbation du vol ${customer.flightNumber} en date du ${customer.flightLocalDepartureDate} reliant ${customer.flightAirportOriginName} à ${customer.flightAirportDestinationName}.

Pour traiter votre demande, il nous manque l'un des deux documents suivants :
la copie ou photo de votre pièce d'identité
la copie ou photo d'au moins un des documents suivants : votre carte d'embarquement, billet électronique, confirmation ou facture de la réservation pour le vol concerné

Transmettez-nous directement vos documents en répondant à cet email.
Sans retour de votre part d'ici une semaine, nous considèrerons que vous ne souhaitez plus nous confier le traitement de votre demande d'indemnisation et clôturerons votre dossier.

Bien cordialement,
Votre équipe AirRefund

Des questions ?
N'hésitez pas à contacter AirRefund !`
  )
}

const missingDocumentsEnglish = (customer) => {
  return (
    `Attention: ${customer.firstName} ${customer.lastName}
Dear ${customer.firstName},
You recently filled out our Compensation Form regarding the ${
    customer.reason === 'cancelled' ? 'cancellation' :
      customer.reason === 'overbooked' ? 'overbooking' : 'delay'
    } of the flight ${customer.flightNumber} departing on ${customer.flightLocalDepartureDate} from ${customer.flightAirportOriginName} to ${customer.flightAirportDestinationName}.

In order to complete your request, we have yet to receive both of the following documents:
a copy or photo of identification document which can be used for travel (i.e. national ID card or passport)
a copy or photo of at least one of the following documents: your boarding pass or passes (if multi-segment) for the entire trip originally purchased, e-ticket, booking confirmation or invoice

Please provide these documents by replying to this email.
If we don't hear from you by next week, we will assume you no longer wish to pursue your claim and we will cancel your request.

Kind regards,
Your AirRefund Team

Any questions?
Please do not hesitate to contact AirRefund
`
  )
}

const missingDocumentsSpanish = (customer) => {
  return (
    `A la atención de: ${customer.firstName} ${customer.lastName}
Estimado/a ${customer.firstName},
Recientemente rellenó nuestro formulario referente ${
    customer.reason === 'cancelled' ? 'a la cancelación ' :
      customer.reason === 'overbooked' ? 'al overbooking' : 'al retraso'
    } de su vuelo ${customer.flightNumber}, el ${customer.flightLocalDepartureDate} desde ${customer.flightAirportOriginName} hacia ${customer.flightAirportDestinationName}.

Para poder procesar su solicitud le rogamos nos envie un correo electrónico adjuntando envie los siguientes documentos:
Una copia de su pasaporte o DNI
Una copia o fotografía de su tarjeta de embarque, billete electrónico o confirmación de vuelo.
Nota: Si en los próximos cinco días laborales no tenemos noticias suyas, asumiremos que no desea seguir con la reclamación y procederemos a la anulación de su solicitud.

Saludos cordiales,
AirRefund

Tiene alguna pregunta?
No dude en contactar con AirRefund
`
  )
}

const missingDocumentsGerman = (customer) => {
  return (
    `Achtung: ${customer.firstName} ${customer.lastName}
Liebe/r ${customer.firstName},
Sie haben kürzlich unser Vergütungsformular in Bezug auf die ${
    customer.reason === 'cancelled' ? 'Stornierung ' :
      customer.reason === 'overbooked' ? 'Überbuchung ' : 'Verspätung'
    } Ihres Fluges Nr.${customer.flightNumber} am ${customer.flightLocalDepartureDate} von ${customer.flightAirportOriginName} nach ${customer.flightAirportDestinationName} ausgefüllt.

Zur Vervollständigung Ihres Antrages fehlt uns eine der folgenden Unterlagen:
eine Kopie oder ein Foto Ihres Personalausweises oder Reisepasses
eine Kopie oder ein Foto von mindestens einem der folgenden Dokumente: Ihre Bordkarte oder E-Ticket, eine Buchungsbestätigung oder ein Reisenachweis

Bitte stellen Sie uns beide Dokumente zur Verfügung, indem Sie auf diese E-Mail antworten.
Sollten wir innerhalb der kommenden Woche keine Rückmeldung von Ihnen erhalten, gehen wir davon aus, dass Sie Ihre Möglichkeit auf eine Entschädigung nicht mehr wahrnehmen möchten und werden daraufhin Ihren Antrag schließen.

Mit freundlichen Grüßen,
Ihr AirRefund- Team
`
  )
}

const sendLOAFrench = (customer) => {
  return (
    `À l'attention de : ${customer.firstName} ${customer.lastName}

Bonjour ${customer.firstName},

Suite au problème rencontré sur le vol ${customer.airlineCode}${customer.flightNumber} reliant ${customer.flightAirportOriginName} (${customer.flightAirportOriginIata}) à ${customer.flightAirportDestinationName} (${customer.flightAirportDestinationIata}) en date du ${customer.flightLocalDepartureDate}, nous pouvons vous aider à obtenir l’indemnisation de ${customer.standardCompensationAmount} €${customer.organizationName === 'SCHNEIDER ELECTRIC' ? ` avant déduction de notre commission de 50%, répartie à parts égales entre l'association Schneider Electric Teachers et AirRefund` : ''} à laquelle vous êtes éligible.

Pour ce faire, nous avons besoin de votre autorisation afin d’échanger avec la compagnie aérienne. En cliquant sur le bouton ci-dessous, vous accéderez à cette autorisation et pourrez la signer simplement et rapidement en quelques clics.

${customer.LOALinks}

Uniquement en cas de succès, nous prélèverons une commission ${customer.organizationName === 'SCHNEIDER ELECTRIC' ?
      `de 50%, répartie à parts égales entre l'association Schneider Electric Teachers et AirRefund.`
      : '.'}
Celle-ci est détaillée dans l'autorisation à signer.
${customer.affiliate.name === 'Visa' ?
      `\nNous vous rappelons que, grâce au partenariat entre Visa et AirRefund, si vous avez payé votre billet d’avion avec une carte Visa, vous bénéficiez de tarifs préférentiels*.\n`
      : ''}
Sachez que le traitement moyen de nos dossiers est de 3 mois. Nous ne manquerons pas de vous tenir informé(e) de toute avancée de votre dossier n°${customer.id}.

Bien cordialement,

Votre équipe AirRefund
${customer.affiliate.name === 'Visa' ?
      `\n* Uniquement 20% de commission pour les détenteurs de cartes Visa Premier, Gold, Platinum ou Infinite et 25% de commission pour toutes les autres cartes Visa.\n`
      : ''}
Des questions ?
N'hésitez pas à contacter AirRefund !`
  )
}

const sendLOAEnglish = (customer) => {
  return (
    `Attention: ${customer.firstName} ${customer.lastName}

Dear ${customer.firstName},

You have recently filled out our Compensation Form regarding the ${
    customer.reason === 'cancelled' ? 'cancellation' :
      customer.reason === 'overbooked' ? 'overbooking' : 'delay'
    } of your flight departing on ${customer.flightLocalDepartureDate} from ${customer.flightAirportOriginName} (${customer.flightAirportOriginIata}) to ${customer.flightAirportDestinationName} (${customer.flightAirportDestinationIata}).

In order to allow us to claim compensation on your behalf, we require you to sign our Letter of Authority. Please click the link below to complete this last step.

${customer.LOALinks}
${customer.affiliate.name === 'Visa' ?
      '\nWe would like to remind you that if you paid for your trip with a Visa card, the commission will be reduced* meaning you get more, thanks to the partnership between Visa and AirRefund.\n'
      : ''}
As soon as we receive your signed Letter of Authority, we will finalise your request and commence discussions with the airline.
It typically takes about three months to complete the process. We’ll keep you up to date on your claim throughout that time.

Kind regards,
Your AirRefund Team
${customer.affiliate.name === 'Visa' ?
      '\n* Only 20% commission for Visa Gold, Platinum or Infinite cards and only 25% commission for all remaining Visa cards.\n'
      : ''}
Any questions?
Please do not hesitate to contact AirRefund`
  )
}

const sendLOASpanish = (customer) => {
  return (
    `A la atención de: ${customer.firstName} ${customer.lastName}
Estimado/a ${customer.firstName},

Recientemente completó el proceso de solicitud de compensación por ${
    customer.reason === 'cancelled' ? 'la cancelación' :
      customer.reason === 'overbooked' ? 'el overbooking' : 'el retraso'
    } del vuelo ${customer.flightNumber} con fecha ${customer.flightLocalDepartureDate} desde ${customer.flightAirportOriginName} (${customer.flightAirportOriginIata}) hacia ${customer.flightAirportDestinationName} (${customer.flightAirportDestinationIata}).

Le informamos que estará representado/a por AirRefund y, para este efecto, es necesario que nos firme de forma electrónica la autorización que encontrará en este enlace:

${customer.LOALinks}

Una vez firmada la autorización, daremos paso a las gestiones con la compañia aérea.
${customer.affiliate.name === 'Visa' ?
      `\nLe recordamos que si pagó su viaje con una tarjeta Visa, usted pagará una menor comisión por la gestión de su reclamación*, gracias al acuerdo entre Visa y AirRefund.\n`
      : ''}
Le mantendremos informado/a de los avances de su caso. Le comunicamos que el proceso suele durar aproximadamente tres meses, dependiendo de las circunstancias.

Saludos cordiales,
AirRefund
${customer.affiliate.name === 'Visa' ?
      `\n* Comisión del 20% para vuelos pagados con tarjeta Visa Oro, Platino o Infinite, 25% para resto de tarjetas Visa.\n`
      : ''}
Tiene alguna pregunta?
No dude en contactar con AirRefund`
  )
}

const sendLOAGerman = (customer) => {
  return (
    `Achtung: ${customer.firstName} ${customer.lastName}

Liebe/r ${customer.firstName},

Sie haben kürzlich unser Vergütungsformular in Bezug auf die ${
    customer.reason === 'cancelled' ? 'Stornierung' :
      customer.reason === 'overbooked' ? 'Überbuchung' : 'Verspätung'
    } Ihres Fluges am ${customer.flightLocalDepartureDate} von ${customer.flightAirportOriginName} (${customer.flightAirportOriginIata}) nach ${customer.flightAirportDestinationName} (${customer.flightAirportDestinationIata}) ausgefüllt.

Mit der Unterzeichnung des Geschäftsbesorgungsantrages, ermöglichen Sie uns, Entschädigung in Ihrem Namen bei der Fluggesellschaft zu behaupten. Bitte klicken Sie auf den unten eingeblendeten Link, um diesen letzten Schritt zu vervollständigen.

${customer.LOALinks}

Sobald wir den von Ihnen unterzeichneten Geschäftsbesorgungsantrag erhalten haben, werden wir Ihre Anfrage fertigstellen und die Verhandlungen mit der Fluggesellschaft beginnen.
${customer.affiliate.name === 'Visa' ?
      `\nWie möchten Sie daran erinnern, dass unsere Provision geringer ist, wenn Sie Ihre Reise mit einer Visa-Karte bezahlt haben*. Das bedeutet, am Ende wird Ihnen ein höherer Betrag ausgezahlt, dank der Partnerschaft zwischen Visa und AirRefund.\n`
      : ''}
Es dauert in der Regel etwa drei Monate, um den Prozess vollständig abzuschließen. In der Zwischenzeit werden wir Sie regelmäßig über den Fortschritt des Prozesses informieren.

Mit freundlichen Grüßen,
Ihr AirRefund- Team
${customer.affiliate.name === 'Visa' ?
      `\n* Nur 20% Provision für Visa Gold, Platinum oder Infinite Karten & nur 25% Kommission für alle anderen Visa- Karten.\n`
      : ''}
Irgendwelche Fragen?
Bitte zögern Sie nicht AirRefund zu kontaktieren`
  )
}

const sendLOAItalian = (customer) => {
  return (
    `All'attenzione di: ${customer.firstName} ${customer.lastName}

Gentile ${customer.firstName},

Hai appena compilato il modulo di richiesta risarcimento relativo al volo ${customer.airlineCode}${customer.flightNumber} in partenza il ${customer.flightLocalDepartureDate} da ${customer.flightAirportOriginName} (${customer.flightAirportOriginIata}) a ${customer.flightAirportDestinationName} (${customer.flightAirportDestinationIata}).

Per permetterci di richiedere un risarcimento a tuo nome, ti chiediamo di firmare la nostra lettera di autorizzazione. Clicca sul link qui sotto per completare quest'ultimo passo.

${customer.LOALinks}
${customer.affiliate.name === 'Visa' ?
      `\nVorremmo ricordarti che se hai pagato il tuo viaggio con una carta Visa, la nostra commissione sarà ridotta*, il che significa che otterai un risarcimento maggiore, grazie alla collaborazione tra Visa ed AirRefund.\n`
      : ''}
In genere, ci vogliono circa tre mesi per completare la procedura. Nel frattempo, ti terremo aggiornato/a sulla tua richiesta.

Cordiali saluti,
Il team di AirRefund
${customer.affiliate.name === 'Visa' ?
      `\n* Soltanto una commissione del 20% per Visa Gold, Platinum o carte Infinite e 25% per tutte le altre carte Visa.\n`
      : ''}
Hai delle domande?
Non esitare a contattare AirRefund`
  )
}

export default emailTemplate;



// WEBPACK FOOTER //
// ./src/utils/emailTemplate.js